import React, { useRef, useEffect, useState } from "react";
import * as Nexus from "nexusui";
import classNames from "classnames";
import { EventEmitter } from "events";
import { isArray } from "lodash";

import "./WeightsControl.scss";
import { Weights } from "../main/core";

interface WeightsControlProps {
  weightKeys: string[];
  weightLabels?: string[];
  weights: Weights;
  onUpdateWeights: (newWeights: Weights) => void;
  events?: EventEmitter;
  noteEventHighlightKey?: string;
  disabled?: boolean;
}
export const WeightsControl: React.FC<WeightsControlProps> = React.memo(
  ({
    weightKeys,
    weightLabels,
    weights,
    onUpdateWeights,
    events,
    noteEventHighlightKey,
    disabled,
  }) => {
    let slidersContainerRef = useRef<HTMLDivElement>(null);
    let [sliders, setSliders] = useState<any>(null);

    useEffect(() => {
      let sliders = new Nexus.Multislider(slidersContainerRef.current, {
        size: [slidersContainerRef.current!.offsetWidth, 70],
        numberOfSliders: weightKeys.length,
        min: 0,
        max: 1,
      });
      if (!disabled) {
        sliders.on("change", (evt: number[]) => {
          if (!isArray(evt)) return;
          onUpdateWeights(
            Object.fromEntries(evt.map((v, idx) => [weightKeys[idx], v]))
          );
        });
      }
      setSliders(sliders);
      return () => {
        sliders.destroy();
        setSliders(null);
      };
    }, [weightKeys, disabled, onUpdateWeights]);

    useEffect(() => {
      if (!weights || !sliders) return;
      sliders.setAllSliders(weightKeys.map((k) => weights[k]));
    }, [weights, sliders, weightKeys]);

    return (
      <div className={classNames("weightsControl", { isDisabled: disabled })}>
        <div
          className="weightsControl--sliders"
          ref={slidersContainerRef}
        ></div>
        <div className="weightsControl--labels">
          {weightKeys.map((k, idx) => (
            <WeightsControlLabel
              key={k}
              weightKey={k}
              label={weightLabels?.[idx] ?? k}
              events={events}
              noteEventHighlightKey={noteEventHighlightKey}
            />
          ))}
        </div>
      </div>
    );
  }
);

interface WeightsControlLabelProps {
  weightKey: string;
  label: string;
  events?: EventEmitter;
  noteEventHighlightKey?: string;
}
const WeightsControlLabel: React.FC<WeightsControlLabelProps> = ({
  weightKey,
  label,
  events,
  noteEventHighlightKey,
}) => {
  let labelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!events || !noteEventHighlightKey) return;
    let onNote = (note: any) => {
      let labelEl = labelRef.current!;
      let val = "" + note[noteEventHighlightKey];
      if (val === weightKey && !labelEl.classList.contains("isPlaying")) {
        labelEl.classList.add("isPlaying");
        setTimeout(() => {
          labelEl.classList.remove("isPlaying");
        }, note.duration * 900);
      } else if (val !== weightKey && labelEl.classList.contains("isPlaying")) {
        labelEl.classList.remove("isPlaying");
      }
    };
    events.on("note", onNote);
    return () => {
      events.off("note", onNote);
    };
  }, [events, noteEventHighlightKey, weightKey]);
  return (
    <div
      ref={labelRef}
      className={classNames("weightsControl--label", {
        isRatio: label.indexOf("/") >= 0,
      })}
    >
      {label.indexOf("/") < 0 ? (
        label
      ) : (
        <>
          <span className="weightsControl--label-upper">
            {label.substring(0, label.indexOf("/"))}
          </span>
          <span className="weightsControl--label-lower">
            {label.substring(label.indexOf("/") + 1)}
          </span>
        </>
      )}
    </div>
  );
};
